import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  EyeIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
  CurrencyRupeeIcon,
  UserCircleIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { cn } from '../lib/utils';


const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';


const getToken = () => {
  return localStorage.getItem('admin_token');
};


// Skeleton Loader Components with Custom Animations
const SkeletonPulse = ({ className = "", delay = 0 }) => (
  <div 
    className={`animate-pulse-shimmer bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] rounded ${className}`}
    style={{ 
      animationDelay: `${delay}ms`,
      animationDuration: '1.5s'
    }}
  />
);

const SkeletonBubble = ({ className = "", delay = 0 }) => (
  <div 
    className={`animate-bubble-expand bg-gradient-to-br from-blue-100 via-blue-200 to-blue-100 rounded-full ${className}`}
    style={{ 
      animationDelay: `${delay}ms`,
      animationDuration: '1.2s'
    }}
  />
);

const SkeletonShimmer = ({ className = "", delay = 0 }) => (
  <div 
    className={`animate-shimmer-wave bg-gradient-to-r from-gray-100 via-white to-gray-100 bg-[length:200%_100%] rounded ${className}`}
    style={{ 
      animationDelay: `${delay}ms`,
      animationDuration: '2s'
    }}
  />
);

const SkeletonGlow = ({ className = "", delay = 0 }) => (
  <div 
    className={`animate-glow-pulse bg-gradient-to-r from-blue-50 via-blue-100 to-blue-50 rounded ${className}`}
    style={{ 
      animationDelay: `${delay}ms`,
      animationDuration: '1.8s'
    }}
  />
);

// Stats Card Skeleton with Bubble Animation
const StatsCardSkeleton = ({ delay = 0 }) => (
  <Card className="animate-fade-in-up" style={{ animationDelay: `${delay}ms` }}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1 space-y-3">
          <SkeletonShimmer className="h-4 w-24" delay={delay} />
          <SkeletonPulse className="h-8 w-20" delay={delay + 100} />
        </div>
        <SkeletonBubble className="w-12 h-12" delay={delay + 50} />
      </div>
    </CardContent>
  </Card>
);

// Table Row Skeleton with Staggered Animation
const TableRowSkeleton = ({ index = 0 }) => {
  const baseDelay = index * 80;
  
  return (
    <TableRow className="animate-fade-in-up hover:bg-gray-50" style={{ animationDelay: `${baseDelay}ms` }}>
      {/* Student Info Column */}
      <TableCell>
        <div className="flex items-center space-x-3">
          <SkeletonBubble className="w-10 h-10 flex-shrink-0" delay={baseDelay} />
          <div className="space-y-2 flex-1">
            <SkeletonShimmer className="h-4 w-32" delay={baseDelay + 50} />
            <SkeletonPulse className="h-3 w-24" delay={baseDelay + 100} />
            <SkeletonGlow className="h-5 w-16 rounded-full" delay={baseDelay + 150} />
          </div>
        </div>
      </TableCell>

      {/* Contact Column */}
      <TableCell>
        <div className="space-y-2">
          <SkeletonShimmer className="h-3 w-40" delay={baseDelay + 50} />
          <SkeletonShimmer className="h-3 w-32" delay={baseDelay + 100} />
          <SkeletonPulse className="h-3 w-28" delay={baseDelay + 150} />
        </div>
      </TableCell>

      {/* Course & Batch Column */}
      <TableCell>
        <div className="space-y-3">
          <div className="border-l-4 border-blue-200 pl-2 space-y-2">
            <SkeletonGlow className="h-4 w-36" delay={baseDelay + 100} />
            <SkeletonShimmer className="h-3 w-28" delay={baseDelay + 150} />
            <SkeletonPulse className="h-3 w-24" delay={baseDelay + 200} />
          </div>
        </div>
      </TableCell>

      {/* Fee Summary Column */}
      <TableCell>
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex justify-between">
              <SkeletonPulse className="h-3 w-20" delay={baseDelay + (i * 50)} />
              <SkeletonShimmer className="h-3 w-16" delay={baseDelay + (i * 50) + 25} />
            </div>
          ))}
        </div>
      </TableCell>

      {/* Status Column */}
      <TableCell>
        <div className="space-y-2">
          <SkeletonGlow className="h-6 w-20 rounded-full" delay={baseDelay + 100} />
          <SkeletonPulse className="h-3 w-24" delay={baseDelay + 150} />
        </div>
      </TableCell>

      {/* Actions Column */}
      <TableCell>
        <div className="space-y-2">
          <SkeletonShimmer className="h-8 w-24 rounded" delay={baseDelay + 100} />
          <SkeletonShimmer className="h-8 w-24 rounded" delay={baseDelay + 150} />
        </div>
      </TableCell>
    </TableRow>
  );
};

// Full Page Skeleton with Orchestrated Animations
const FullPageSkeleton = () => (
  <div className="space-y-6">
    {/* Header Skeleton */}
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="space-y-2">
          <SkeletonShimmer className="h-8 w-64" />
          <SkeletonPulse className="h-4 w-96" delay={100} />
        </div>
        <div className="flex gap-3">
          <SkeletonGlow className="h-10 w-64" delay={150} />
          <SkeletonShimmer className="h-10 w-20" delay={200} />
        </div>
      </div>

      {/* Filters Skeleton */}
      <div className="flex flex-wrap gap-2 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
        <SkeletonPulse className="h-4 w-32" delay={250} />
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <SkeletonGlow key={i} className="h-8 w-20 rounded" delay={300 + (i * 40)} />
        ))}
      </div>
    </div>

    {/* Stats Cards Skeleton */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[0, 1, 2, 3].map((i) => (
        <StatsCardSkeleton key={i} delay={i * 100} />
      ))}
    </div>

    {/* Table Skeleton */}
    <div className="overflow-x-auto border rounded-lg animate-fade-in-up" style={{ animationDelay: '400ms' }}>
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-800">
            {['Student Info', 'Contact', 'Course & Batch', 'Fee Summary', 'Status', 'Actions'].map((header, i) => (
              <TableHead key={i} className="text-white">
                <SkeletonShimmer className="h-4 w-24 bg-gray-700" delay={i * 50} />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <TableRowSkeleton key={i} index={i} />
          ))}
        </TableBody>
      </Table>
    </div>

    {/* Pagination Skeleton */}
    <div className="flex justify-center items-center space-x-2 mt-4 animate-fade-in-up" style={{ animationDelay: '600ms' }}>
      <SkeletonGlow className="h-9 w-20 rounded" />
      {[0, 1, 2, 3, 4].map((i) => (
        <SkeletonShimmer key={i} className="h-9 w-10 rounded" delay={i * 40} />
      ))}
      <SkeletonGlow className="h-9 w-16 rounded" delay={200} />
    </div>
  </div>
);


// API service functions for Student Fee Records
const studentFeeApiService = {
  // Get all students with fee summary
  async getAllStudentsWithFeeSummary(page = 1, limit = 10, search = '', status = '') {
    const token = getToken();
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...(status && { status })
    });

    const response = await fetch(`${API_BASE_URL}/student-fees/fee-summary?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch students');
    }
    
    return response.json();
  },

  // Generate student fee report PDF
  async generateStudentFeeReport(studentId) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/student-fees/${studentId}/fee-report`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to generate report');
    }
    
    return response.blob();
  },

  // Get student complete details
  async getStudentCompleteDetails(studentId) {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/student-fees/${studentId}/complete-details`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch student details');
    }
    
    return response.json();
  }
};


const StudentFeeRecords = ({ onViewStudentDetails }) => {
  const currentUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isCounselor = currentUser?.role === 'counselor';
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [generatingReport, setGeneratingReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState({
    totalFeeCollected: 0,
    totalPendingAmount: 0,
    activeStudents: 0,
    pendingPayments: 0
  });

  // Fetch students data
  useEffect(() => {
    fetchStudents();
  }, [currentPage, searchTerm, statusFilter]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const data = await studentFeeApiService.getAllStudentsWithFeeSummary(
        currentPage, 
        10, 
        searchTerm,
        statusFilter
      );
      
      // console.log('Fetched students data:', data);
      
      setStudents(data.students || []);
      setTotalPages(data.totalPages || 1);
      setTotalStudents(data.totalStudents || 0);
      
      calculateStats(data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error(error.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (studentsList) => {
    const totalFeeCollected = studentsList.reduce((sum, student) => 
      sum + (student.feeSummary?.paidAmount || 0), 0
    );
    
    const totalPendingAmount = studentsList.reduce((sum, student) => 
      sum + (student.feeSummary?.pendingAmount || 0), 0
    );
    
    const activeStudents = studentsList.filter(s => s.status === 'active').length;
    
    const pendingPayments = studentsList.filter(s => 
      s.feeSummary?.overallStatus === 'pending' || 
      s.feeSummary?.overallStatus === 'overdue' ||
      s.feeSummary?.overallStatus === 'partial'
    ).length;

    setStats({
      totalFeeCollected,
      totalPendingAmount,
      activeStudents,
      pendingPayments
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handleGenerateReport = async (studentId, studentName) => {
    try {
      if (!studentId) {
        toast.error('Student ID is missing');
        return;
      }
      
      setGeneratingReport(studentId);
      const blob = await studentFeeApiService.generateStudentFeeReport(studentId);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fee-report-${studentName.replace(/\s+/g, '-')}-${studentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Fee report generated for ${studentName}`);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.message || 'Failed to generate fee report');
    } finally {
      setGeneratingReport(null);
    }
  };

  const handleViewDetails = async (student) => {
    try {
      // console.log('Student object in handleViewDetails:', student);
      
      if (!student || !student._id) {
        console.error('Student or student._id is undefined:', student);
        toast.error('Student information is incomplete');
        return;
      }

      if (onViewStudentDetails) {
        // console.log('Calling onViewStudentDetails with student ID:', student._id);
        onViewStudentDetails(student);
      }
    } catch (error) {
      console.error('Error in handleViewDetails:', error);
      toast.error(error.message || 'Failed to load student details');
    }
  };

  const getFeeStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'partial': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'overdue': return 'bg-red-100 text-red-800 border-red-200';
      case 'no-data': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStudentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive': return 'bg-red-100 text-red-800 border-red-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const renderPagination = () => {
    if (totalStudents === 0) return null;

    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage   = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? 'default' : 'outline'}
          size="sm"
          onClick={() => setCurrentPage(i)}
          className="mx-1 min-w-[36px]"
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="flex justify-center items-center gap-1 mt-4 flex-wrap">
        <Button
          variant="outline" size="sm"
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
        >← Prev</Button>

        {startPage > 1 && (
          <>
            <Button variant="outline" size="sm" className="min-w-[36px]" onClick={() => setCurrentPage(1)}>1</Button>
            {startPage > 2 && <span className="text-gray-400 px-1">…</span>}
          </>
        )}

        {pages}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="text-gray-400 px-1">…</span>}
            <Button variant="outline" size="sm" className="min-w-[36px]" onClick={() => setCurrentPage(totalPages)}>{totalPages}</Button>
          </>
        )}

        <Button
          variant="outline" size="sm"
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
        >Next →</Button>
      </div>
    );
  };

  const hasActiveFilters = searchTerm || statusFilter;

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="text-2xl">Student Fee Records</CardTitle>
              <CardDescription>
                View and manage all student fee records with complete payment history
              </CardDescription>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name, ID, email, phone..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10 pr-4"
                />
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="flex items-center space-x-2"
              >
                <XMarkIcon className="h-4 w-4" />
                <span>Clear</span>
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
            </div>
            {['', 'active', 'inactive', 'completed'].map(status => (
              <Button
                key={status}
                variant={statusFilter === status ? "default" : "outline"}
                size="sm"
                onClick={() => handleStatusFilter(status)}
              >
                {status === '' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
            {['paid', 'pending', 'overdue', 'partial'].map(status => (
              <Button
                key={status}
                variant={statusFilter === status ? "default" : "outline"}
                size="sm"
                onClick={() => handleStatusFilter(status)}
              >
                Fee: {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <FullPageSkeleton />
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Students</p>
                      <p className="text-2xl font-bold text-gray-900">{totalStudents}</p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <UserCircleIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Active Students</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.activeStudents}</p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg">
                      <UserCircleIcon className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {!isCounselor && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Collected</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(stats.totalFeeCollected)}
                        </p>
                      </div>
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CurrencyRupeeIcon className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {!isCounselor && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Pending Payments</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(stats.totalPendingAmount)}
                        </p>
                      </div>
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <CurrencyRupeeIcon className="h-6 w-6 text-yellow-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Pagination info bar */}
            {totalStudents > 0 && (
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2 px-1">
                <span>
                  Showing{' '}
                  <strong>{Math.min((currentPage - 1) * 10 + 1, totalStudents)}</strong>–
                  <strong>{Math.min(currentPage * 10, totalStudents)}</strong>{' '}
                  of <strong>{totalStudents}</strong> student{totalStudents !== 1 ? 's' : ''}
                </span>
                {totalPages > 1 && (
                  <span>Page {currentPage} of {totalPages}</span>
                )}
              </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white/60 px-2 py-2 sm:px-4">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-800 hover:bg-gray-800">
                    <TableHead className="text-white font-semibold">Student Info &amp; ID</TableHead>
                    <TableHead className="text-white font-semibold">Contact</TableHead>
                    <TableHead className="text-white font-semibold">Course & Batch</TableHead>
                    <TableHead className="text-white font-semibold">Fee Summary</TableHead>
                    <TableHead className="text-white font-semibold">Status</TableHead>
                    <TableHead className="text-center text-white font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student) => (
                    <TableRow key={student._id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <UserCircleIcon className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{student.name}</p>
                            {student.studentId && (
                              <p className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded w-fit mb-0.5">
                                {student.studentId}
                              </p>
                            )}
                            <p className="text-sm text-gray-500">
                              Joined: {formatDate(student.createdAt)}
                            </p>
                            <Badge variant="outline" className={getStudentStatusColor(student.status)}>
                              {student.status}
                            </Badge>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-900">{student.email}</p>
                          <p className="text-sm text-gray-600">{student.phone}</p>
                          {student.guardian?.name && (
                            <p className="text-xs text-gray-500">
                              Guardian: {student.guardian.name}
                            </p>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="space-y-2">
                          {student.courses && student.courses.length > 0 ? (
                            student.courses.map((course, index) => (
                              <div key={index} className="border-l-4 border-blue-500 pl-2">
                                <div className="flex items-center space-x-1 mb-1">
                                  <AcademicCapIcon className="h-4 w-4 text-gray-500" />
                                  <p className="font-medium text-sm text-gray-900">{course.courseName}</p>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <CalendarDaysIcon className="h-3 w-3 text-gray-400" />
                                  <p className="text-xs text-gray-600">{course.batchName}</p>
                                </div>
                                <p className="text-xs text-gray-500">
                                  {course.batchMode} • {course.duration} months
                                </p>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-gray-500 italic">No courses assigned</p>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Total Fee:</span>
                            <span className="font-semibold">
                              {formatCurrency(student.feeSummary?.totalFee || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Paid:</span>
                            <span className="font-semibold text-green-600">
                              {formatCurrency(student.feeSummary?.paidAmount || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Pending:</span>
                            <span className="font-semibold text-red-600">
                              {formatCurrency(student.feeSummary?.pendingAmount || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Installments:</span>
                            <span className="font-semibold">
                              {student.feeSummary?.paidInstallments || 0}/{student.feeSummary?.totalInstallments || 0}
                            </span>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell className="align-top">
                        <div className="flex max-w-[14rem] flex-col gap-2">
                          <span
                            className={cn(
                              'fee-status-badge border',
                              getFeeStatusColor(student.feeSummary?.overallStatus)
                            )}
                          >
                            {(student.feeSummary?.overallStatus || 'no data')
                              .toString()
                              .replace(/-/g, ' ')
                              .toUpperCase()}
                          </span>
                          {student.feeSummary?.nextDueDate && (
                            <div className="text-xs leading-snug text-gray-600">
                              Next due: {formatDate(student.feeSummary.nextDueDate)}
                            </div>
                          )}
                          {student.feeSummary?.overdueAmount > 0 && (
                            <div className="text-xs font-semibold leading-snug text-red-600">
                              Overdue {formatCurrency(student.feeSummary.overdueAmount)}
                            </div>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className="text-center align-middle">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetails(student)}
                            className="inline-flex items-center justify-center gap-1"
                            disabled={!student._id}
                          >
                            <EyeIcon className="h-4 w-4" />
                            <span>Details</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleGenerateReport(student._id, student.name)}
                            disabled={generatingReport === student._id || !student._id}
                            className="inline-flex items-center justify-center gap-1"
                          >
                            <DocumentArrowDownIcon className="h-4 w-4" />
                            <span>
                              {generatingReport === student._id ? 'Generating...' : 'PDF Report'}
                            </span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {students.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12">
                        <div className="flex flex-col items-center space-y-3">
                          <UserCircleIcon className="h-16 w-16 text-gray-300" />
                          <p className="text-gray-500 text-lg">No students found</p>
                          <p className="text-gray-400 text-sm">
                            {hasActiveFilters ? 'Try adjusting your search filters' : 'No student records available'}
                          </p>
                          {hasActiveFilters && (
                            <Button variant="outline" onClick={clearFilters}>
                              Clear Filters
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {renderPagination()}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StudentFeeRecords;
